/* eslint-disable camelcase */
import stripHtmlFromString from '../../../helpers/stripHtml.helper';
import { validateObject } from '../../../helpers/objectValidator.helper';
import { getCMSConfig, getConsentConfig } from '../../../helpers/windowObject.helper';
import { Address, Characteristic, FormDataType, IRelatedParty, LeadManagementDTO } from '../interface';
import { getFootnoteTextArray } from '../../../components/Footnote';
import { ConsentName } from '../enum';
import { isString } from './validateForm.helper';

const getFootnoteIndexArray = (legalText: string): string[] | null => {
    // only numbers between '>' and '<' chars
    const footnoteIndexRegex: RegExp = /(?!\>)(\d)(?=\<)/g;

    return legalText.match(footnoteIndexRegex);
};

const generateLegalTextWithFootnotes = (legalText: string) => {
    const footnoteIndexes = getFootnoteIndexArray(legalText);
    const footnoteTexts = getFootnoteTextArray();
    let generatedText = stripHtmlFromString(legalText);

    // this involves DOM manipulation, should be tested with cypress
    /* istanbul ignore next */
    footnoteIndexes && footnoteIndexes.forEach(stringIndex => {
        // get footnote text
        const footnote = stripHtmlFromString(footnoteTexts[Number(stringIndex) - 1]);
        // concatenate to legal text by this format: '|(1)Lorem ipsum...'
        generatedText += `|(${stringIndex})${footnote}`;
    });

    return generatedText;
};

const getCharacteristicsArray = (characteristics: Object): Characteristic[] => {
    const characteristicArray: Characteristic[] = [];

    for (const key in characteristics) {
        const value = characteristics[key as keyof Object];
        value !== undefined && characteristicArray.push(
            {
                name: key,
                valueType: typeof value,
                value,
            }
        );
    }

    return characteristicArray;
};

export const mapFormValues = (formData: FormDataType, address: Address): LeadManagementDTO => {
    const {
        role,
        givenName,
        familyName,
        phoneNumber,
        emailAddress,
        streetName,
        streetNr,
        postcode,
        city,
        description,
        original_sales_unit,
        next_action,
        streetSuffix,
    } = formData;

    let {
        productInterest,
        consulting_consent,
        dev_consent,
    } = formData;

    // TODO: recheck consents
    const cmsConfig = getCMSConfig();

    // if it is not filled get fallback value from config
    productInterest = productInterest || cmsConfig.productInterest;

    const consentConfig = getConsentConfig(ConsentName.PRIVACY_NOTICE);

    // handle consent values if customer not clicked on the checkbox, alias missing from formData
    // in case the boolean is false then verion should be 0 if true then it will come from config
    if (!consulting_consent) {
        consulting_consent = false;
    }

    if (!dev_consent) {
        dev_consent = false;
    }

    const characteristics = {
        next_action,
        sales_action: getCMSConfig().salesAction,
        original_sales_unit,
        // TODO: remove after backend adjustment | START
        consulting_consent,
        consent_version: '0', // advertisingConfig.version,
        advertising_text: '', // generateLegalTextWithFootnotes(advertisingConfig.text),
        dev_consent,
        dev_consent_version: '0', // personalOfferConfig.version,
        dev_text: '', // generateLegalTextWithFootnotes(personalOfferConfig.text),
        privacy_notice_consent: true, // Always true, we just perform a frontend check
        privacy_notice_text: consentConfig
            ? generateLegalTextWithFootnotes(consentConfig.text)
            : '',
        // TODO: remove after backend adjustment | END
    };

    // only valid objects can be pushed to prospectContact array
    const prospectContact = [];

    if (city) {
        prospectContact.push({
            mediumType: 'postalAddress',
            characteristic: {
                geographicAddress: {
                    city,
                    postcode,
                    streetNr,
                    streetName,
                    streetSuffix,
                },
            },
        });

        if (address) {
            prospectContact[0].characteristic.geographicAddress.geographicLocation = {
                id: address.addressid,
                '@type': 'GeoJsonPoint',
            };
        }
    }

    emailAddress && prospectContact.push({
        mediumType: 'emailAddress',
        characteristic: {
            emailAddress,
        },
    });

    phoneNumber && prospectContact.push({
        mediumType: 'phoneNumber',
        characteristic: {
            phoneNumber,
            contactType: 'fixed',
        },
    });

    const payload: LeadManagementDTO = {
        status: cmsConfig?.statusField || 'accepted',
        name: 'OVE-Formular',
        description: isString(description) ? description : '',
        referredDate: new Date().toISOString(),
        relatedParty: [
            {
                id: '1',
                givenName: isString(givenName) ? givenName : '',
                familyName: isString(familyName) ? familyName : '',
                role: isString(role) ? role : '',
                '@referredType': 'Contact',
            },
        ],
        prospectContact,
        product: {
            id: '1',
            name: isString(productInterest) ? productInterest : '',
        },
        characteristic: getCharacteristicsArray(characteristics),
    };

    return payload;
};

// TODO: not in use! -> delete or setup validation
export const validateRelatedParty = (relatedParty: any): relatedParty is IRelatedParty => {
    const requiredKeys = [
        { key: 'givenName', type: 'string' },
        { key: 'familyName', type: 'string' },
        { key: 'role', type: 'string' },
    ];

    return validateObject(requiredKeys, relatedParty);
};
